<template>
  <canvas
      ref="canvas"
      class="fireworks-canvas"
      @click="addFirework"
      :class="{ 'day-mode': dayMode }"
  ></canvas>
</template>

<script>
export default {
  name: 'Fireworks',
  data() {
    return {
      activeFireworks: [],
      lastFrameTime: 0,
      gravity: 0.1,
      friction: 0.98,
      canvas: null,
      ctx: null,
      dayMode: false,
      autoLaunchInterval: null
    }
  },
  mounted() {
    this.initCanvas();
    this.startAnimation();
    this.startAutoLaunch();
    this.detectDayMode();
  },
  methods: {
    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.resize();
      window.addEventListener('resize', this.resize);
    },

    resize() {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    },

    startAutoLaunch() {
      this.autoLaunchInterval = setInterval(() => {
        if (this.activeFireworks.length < 5) {
          this.triggerFirework();
        }
      }, 800);
    },

    detectDayMode() {
      // 根据当前时间自动检测白天/夜晚模式
      // const hours = new Date().getHours();
      // this.dayMode = hours >= 6 && hours < 18; // 6am-6pm为白天
      this.dayMode = true
    },

    toggleDayMode() {
      this.dayMode = !this.dayMode;
    },

    triggerFirework() {
      const x = Math.random() * this.canvas.width;
      this.activeFireworks.push(this.createFirework(x));
    },

    addFirework(e) {
      const x = e.clientX || e.touches?.[0]?.clientX || Math.random() * this.canvas.width;
      this.activeFireworks.push(this.createFirework(x));
    },

    createFirework(startX) {
      return {
        x: startX,
        y: this.canvas.height,
        targetY: this.canvas.height * 0.3 + Math.random() * 100,
        particles: [],
        color: this.getRandomColor(),
        state: 'rising',
        progress: 0,
        speed: 3 + Math.random() * 2,
        trail: []
      };
    },

    startAnimation() {
      const animate = (timestamp) => {
        const deltaTime = timestamp - this.lastFrameTime;
        this.lastFrameTime = timestamp;

        // 透明清除画布
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // 白天模式添加轻微背景模糊效果
        if (this.dayMode) {
          this.ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
          this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }

        this.updateFireworks(deltaTime);
        requestAnimationFrame(animate);
      };
      animate(0);
    },

    updateFireworks(deltaTime) {
      const timeScale = deltaTime / 16;

      for (let i = this.activeFireworks.length - 1; i >= 0; i--) {
        const fw = this.activeFireworks[i];

        if (fw.state === 'rising') {
          this.updateRisingFirework(fw, timeScale);
        }
        else if (fw.state === 'exploding') {
          this.updateExplodingParticles(fw, timeScale);
        }

        if (fw.state === 'done') {
          this.activeFireworks.splice(i, 1);
        }
      }
    },

    updateRisingFirework(fw, timeScale) {
      fw.y -= fw.speed * timeScale;

      // 添加轨迹点
      fw.trail.push({
        x: fw.x,
        y: fw.y,
        alpha: 1
      });

      // 绘制轨迹
      this.drawRocketTrail(fw);

      // 绘制火箭
      this.drawRocket(fw);

      if (fw.y <= fw.targetY) {
        fw.state = 'exploding';
        this.createExplosion(fw);
      }
    },

    drawRocketTrail(fw) {
      fw.trail.forEach(point => {
        point.alpha -= 0.05;
      });

      fw.trail = fw.trail.filter(point => point.alpha > 0);

      this.ctx.save();
      this.ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < fw.trail.length; i++) {
        const point = fw.trail[i];
        const size = 3 * point.alpha;

        this.ctx.beginPath();
        this.ctx.fillStyle = `rgba(255, 150, 50, ${point.alpha})`;
        this.ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
        this.ctx.fill();
      }
      this.ctx.restore();
    },

    drawRocket(fw) {
      this.ctx.save();
      this.ctx.globalCompositeOperation = 'lighter';

      // 火箭主体
      this.ctx.beginPath();
      this.ctx.fillStyle = fw.color;
      this.ctx.arc(fw.x, fw.y, 3, 0, Math.PI * 2);

      // 白天模式添加黑色描边增强可见度
      if (this.dayMode) {
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();
      }
      this.ctx.fill();

      // 火箭尾焰
      const gradient = this.ctx.createRadialGradient(
          fw.x, fw.y + 5, 0,
          fw.x, fw.y + 5, 5
      );
      gradient.addColorStop(0, 'rgba(255, 200, 50, 0.8)');
      gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');

      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(fw.x, fw.y + 5, 5, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.restore();
    },

    createExplosion(fw) {
      const particleCount = this.dayMode ? 100 + Math.floor(Math.random() * 50) : 80 + Math.floor(Math.random() * 40);
      const baseHue = this.getHueFromColor(fw.color);

      for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 2;
        const size = this.dayMode ?
            Math.random() * 4 + 2 : // 白天模式粒子更大
            Math.random() * 3 + 1;

        // 颜色设置
        const hueVariation = this.dayMode ?
            Math.random() * 20 - 10 : // 白天模式色相变化更小
            Math.random() * 30 - 15;

        const hue = baseHue + hueVariation;
        const saturation = this.dayMode ?
            90 + Math.random() * 10 : // 白天更高饱和度
            80 + Math.random() * 20;

        const lightness = this.dayMode ?
            40 + Math.random() * 10 : // 白天更暗更强烈
            50 + Math.random() * 20;

        fw.particles.push({
          x: fw.x,
          y: fw.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: size,
          color: `hsl(${hue},${saturation}%, ${lightness}%)`,
          life: 100 + Math.random() * 50,
          maxLife: 100 + Math.random() * 50,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
          isDayMode: this.dayMode // 标记当前模式
        });
      }
    },

    updateExplodingParticles(fw, timeScale) {
      let hasAliveParticles = false;

      this.ctx.save();
      this.ctx.globalCompositeOperation = 'lighter';

      fw.particles.forEach(p => {
        p.vy += this.gravity * timeScale;
        p.vx *= this.friction;
        p.vy *= this.friction;
        p.x += p.vx * timeScale;
        p.y += p.vy * timeScale;
        p.rotation += p.rotationSpeed * timeScale;
        p.life -= timeScale;

        if (p.life > 0) {
          hasAliveParticles = true;
          const alpha = p.life / p.maxLife;
          const currentSize = p.size * alpha;

          // 粒子发光效果
          this.ctx.beginPath();
          this.ctx.fillStyle = p.color;
          this.ctx.globalAlpha = p.isDayMode ? alpha * 0.9 : alpha * 0.7;
          this.ctx.arc(p.x, p.y, p.isDayMode ? currentSize * 2.2 : currentSize * 1.5, 0, Math.PI * 2);
          this.ctx.fill();

          // 粒子核心
          this.ctx.beginPath();
          this.ctx.fillStyle = p.isDayMode ?
              `rgba(255, 255, 255, ${alpha * 0.9})` :
              `rgba(255, 255, 255, ${alpha})`;
          this.ctx.arc(p.x, p.y, p.isDayMode ? currentSize * 0.8 : currentSize * 0.5, 0, Math.PI * 2);
          this.ctx.fill();

          // 白天模式添加黑色描边增强可见度
          if (p.isDayMode) {
            this.ctx.strokeStyle = `rgba(0, 0, 0, ${alpha * 0.5})`;
            this.ctx.lineWidth = 1;
            this.ctx.stroke();
          }
        }
      });

      this.ctx.restore();
      if (!hasAliveParticles) {
        fw.state = 'done';
      }
    },

    getRandomColor() {
      // 夜晚模式颜色（更明亮）
      const nightColors = [
        '#FF5252', '#FFD740', '#64FFDA',
        '#448AFF', '#B388FF', '#FF80AB'
      ];

      // 白天模式颜色（更饱和、更深）
      const dayColors = [
        '#FF0000', '#FF6B00', '#FFD800',
        '#00A8FF', '#8A2BE2', '#FF1493',
        '#FF4500', '#FF8C00', '#32CD32'
      ];

      return this.dayMode
          ? dayColors[Math.floor(Math.random() * dayColors.length)]
          : nightColors[Math.floor(Math.random() * nightColors.length)];
    },

    getHueFromColor(hexColor) {
      const r = parseInt(hexColor.slice(1, 3), 16) / 255;
      const g = parseInt(hexColor.slice(3, 5), 16) / 255;
      const b = parseInt(hexColor.slice(5, 7), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h;

      if (max === min) {
        h = 0;
      } else if (max === r) {
        h = 60 * (0 + (g - b) / (max - min));
      } else if (max === g) {
        h = 60 * (2 + (b - r) / (max - min));
      } else {
        h = 60 * (4 + (r - g) / (max - min));
      }

      if (h < 0) h += 360;
      return h;
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize);
    clearInterval(this.autoLaunchInterval);
  }
};
</script>

<style scoped>
.fireworks-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 999;
  background: transparent;
  mix-blend-mode: screen;
}

/* 白天模式样式 */
.fireworks-canvas.day-mode {
  mix-blend-mode: normal;
}
</style>